// Lightweight markdown renderer for Wingman chat bubbles.
// Handles: **bold**, *italic*, `code`, --- (hr), - bullets, 1. ordered lists,
// blank-line paragraphs. NO heavy deps, NO HTML injection.
import React from 'react';

// Parse inline markdown (bold, italic, code) into React nodes.
// Order matters: code first (so its contents aren't re-parsed for bold/italic).
function renderInline(text) {
  if (!text) return null;
  const out = [];
  // Token regex: `code` | **bold** | *italic*
  const re = /(`[^`\n]+`|\*\*[^*\n]+\*\*|\*[^*\n]+\*)/g;
  let last = 0;
  let match;
  let i = 0;
  while ((match = re.exec(text)) !== null) {
    if (match.index > last) out.push(text.slice(last, match.index));
    const tok = match[0];
    if (tok.startsWith('`') && tok.endsWith('`')) {
      out.push(
        <code key={`c${i}`} className="px-1 py-0.5 rounded bg-neutral-100 text-neutral-800 mono text-[0.92em]">
          {tok.slice(1, -1)}
        </code>
      );
    } else if (tok.startsWith('**') && tok.endsWith('**')) {
      out.push(<strong key={`b${i}`} className="font-bold text-neutral-900">{tok.slice(2, -2)}</strong>);
    } else if (tok.startsWith('*') && tok.endsWith('*')) {
      out.push(<em key={`i${i}`} className="italic">{tok.slice(1, -1)}</em>);
    }
    last = match.index + tok.length;
    i += 1;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

// Group raw lines into block elements (paragraphs, lists, hr).
export default function Markdown({ text }) {
  if (!text) return null;
  // Strip extraneous backtick lines that the model sometimes emits as bullet placeholders.
  const lines = String(text).split('\n').map((l) => l.replace(/^\s*`\s*$/, ''));

  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    // Horizontal rule
    if (/^---+$/.test(trimmed)) {
      blocks.push({ type: 'hr' });
      i += 1;
      continue;
    }

    // Blank line — paragraph separator
    if (trimmed === '') {
      i += 1;
      continue;
    }

    // Bullet list (- or * followed by space)
    if (/^[-*]\s+/.test(trimmed)) {
      const items = [];
      while (i < lines.length && /^[-*]\s+/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^[-*]\s+/, ''));
        i += 1;
      }
      blocks.push({ type: 'ul', items });
      continue;
    }

    // Ordered list (1. 2. ...)
    if (/^\d+\.\s+/.test(trimmed)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^\d+\.\s+/, ''));
        i += 1;
      }
      blocks.push({ type: 'ol', items });
      continue;
    }

    // Paragraph — accumulate until blank line, list, or hr.
    const para = [];
    while (i < lines.length) {
      const t = lines[i].trim();
      if (t === '' || /^---+$/.test(t) || /^[-*]\s+/.test(t) || /^\d+\.\s+/.test(t)) break;
      para.push(lines[i]);
      i += 1;
    }
    blocks.push({ type: 'p', text: para.join(' ').trim() });
  }

  return (
    <div className="space-y-2.5">
      {blocks.map((b, idx) => {
        if (b.type === 'hr') return <hr key={idx} className="border-0 border-t hairline my-1" />;
        if (b.type === 'ul') {
          return (
            <ul key={idx} className="space-y-1 pl-4">
              {b.items.map((it, j) => (
                <li key={j} className="relative">
                  <span className="absolute -left-3 top-[0.55em] inline-block h-1 w-1 rounded-full bg-neutral-400" />
                  {renderInline(it)}
                </li>
              ))}
            </ul>
          );
        }
        if (b.type === 'ol') {
          return (
            <ol key={idx} className="space-y-1 pl-5">
              {b.items.map((it, j) => (
                <li key={j} className="relative">
                  <span className="absolute -left-5 top-0 mono text-[11px] font-bold text-neutral-500 w-4 text-right">{j + 1}.</span>
                  {renderInline(it)}
                </li>
              ))}
            </ol>
          );
        }
        return <p key={idx} className="leading-relaxed">{renderInline(b.text)}</p>;
      })}
    </div>
  );
}
